import { LoopReducer, loop, Loop, Cmd } from "redux-loop";
import {
  ActionType,
  createAsyncAction,
  getType,
  createAction,
} from "typesafe-actions";
import {
  getAllEventsAsync,
  getAllLyytiEventsAsync,
  getAllLyytiParticipantsAsync,
} from "../events/listEvents/listEventsReducer";
import { getParticipants } from "../reducers/participantReducer";
import certService from "../services/certs";
import eventService from "../services/events";
import { historyService } from "../services/historyService";
import {
  Cert,
  CertMailTable,
  CertNewResponse,
  CertOperationType,
  CertSource,
  CertState,
  CertTemplateDefaultValues,
  CustomerCertTemplate,
  lyytiParticipant,
} from "../types/types";
import { convertParticipantToListFormat, sortByKey } from "../utils/functions";
import ConsoleHelper from "../utils/logger";

const startingSourceOptions = ["", "CSV-import", "ContactMate"];
const initialState: CertState = {
  initLoading: false,
  selectedTemplate: "",
  certSource: {
    options: startingSourceOptions,
    selected: "",
  },
  event: {
    selected: "",
    participants: [],
    selectedParticipants: [],
  },
  sendMultiple: false,
  allCerts: [],
};

const emptyCustomerTemplate = {
  templateName: "",
  templateId: "",
  templateDescription: "",
  templateDataModel: {
    templatePreviewUrl: "",
    templateQuestions: [],
  },
  postmarkTemplateId: "",
  postmarkTemplateName: "",
  postmarkTemplateDescription: "",
  postmarkTemplateDataModel: {
    postmarkTemplateQuestions: [],
  },
};

export const getCertNew = createAsyncAction(
  "START_CERT_NEW_FETCH",
  "CERT_NEW_FETCH_COMPLETE",
  "CERT_NEW_FETCH_FAIL"
)<
  string | null,
  { data: CertNewResponse; status: number; template: string | null },
  Error
>();

export type CertSendActionPayload = {
  certOperationId: string;
  certOperationType: CertOperationType;
  template: CustomerCertTemplate;
  selected: { [key: string]: string }[];
  preview: boolean;
};
export const newCertSend = createAsyncAction(
  "START_CERT_SEND_REQUEST",
  "CERT_SEND_COMPLETE",
  "CERT_SEND_FAIL"
)<CertSendActionPayload, { data: any; status: number }, Error>();

export const resendCert = createAsyncAction(
  "START_RESEND_CERT",
  "RESEND_CERT_COMPLETE",
  "RESEND_CERT_FAIL"
)<string, { data: any; status: number }, Error>();

export const getLyytikey = createAsyncAction(
  "START_GET_LYYTIKEY_REQUEST",
  "GET_LYYTIKEY_REQUEST_COMPLETE",
  "GET_LYYTIKEY_REQUEST_FAIL"
)<undefined, { data: any; status: number }, Error>();

export const getAllCerts = createAsyncAction(
  "START_GET_ALL_CERTS_FETCH",
  "GET_ALL_CERTS_FETCH_COMPLETE",
  "GET_ALL_CERTS_FETCH_FAIL"
)<undefined, { data: Cert[]; status: number }, Error>();

const toggleRecipient = createAction("TOGGLE_CERT_RECIPIENT")<string>();
const toggleAllRecipients = createAction(
  "TOGGLE_ALL_CERT_RECIPIENTS"
)<undefined>();
const startNewCertSend = createAction("START_CERT_SEND")<{
  id: string;
  preview: boolean;
}>();
const changeEventSource = createAction("CHANGE_EVENT_SOURCE")<CertSource>();
const changeSendType = createAction("CHANGE_CERT_SEND_TYPE")<undefined>();
const changeEvent = createAction("CHANGE_EVENT_CERT")<{
  eventId: string;
  eventSource: CertSource;
  eventName: string;
}>();
const mapCertEditData = createAction("MAP_CERT_EDIT_DATA")<{
  mailId: string;
  certOperationId: string;
}>();
const importParticipants = createAction("IMPORT_PARTICIPANTS")<any[]>();
export const setErrorMessageMissingCertFields = createAction(
  "SET_ERROR_MESSAGE_CERT_MISSING_REQUIRED_FIELD"
)<string[]>();
export const changeSelectedTemplate = createAction(
  "CHANGE_SELECTED_TEMPLATE"
)<string>();
export const setCertTemplateDefaultValues = createAction(
  "SET_CERT_TEMPLATE_DEFAULT_VALUES"
)<CertTemplateDefaultValues>();

type Action =
  | ActionType<typeof getCertNew>
  | ActionType<typeof newCertSend>
  | ActionType<typeof getLyytikey>
  | ActionType<typeof getAllLyytiEventsAsync>
  | ActionType<typeof getAllEventsAsync>
  | ActionType<typeof getAllLyytiParticipantsAsync>
  | ActionType<typeof getParticipants>
  | ActionType<typeof getAllCerts>
  | ActionType<typeof resendCert>
  | ActionType<typeof importParticipants>
  | ActionType<typeof mapCertEditData>
  | ActionType<typeof setErrorMessageMissingCertFields>
  | ActionType<typeof toggleRecipient>
  | ActionType<typeof toggleAllRecipients>
  | ActionType<typeof startNewCertSend>
  | ActionType<typeof changeEventSource>
  | ActionType<typeof changeSendType>
  | ActionType<typeof changeSelectedTemplate>
  | ActionType<typeof changeEvent>;

export const certReducer: LoopReducer<CertState, Action> = (
  state: CertState = initialState,
  action: Action
): CertState | Loop<CertState> => {
  switch (action.type) {
    case getType(getCertNew.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(certService.getCertNew, {
          successActionCreator: getCertNew.success,
          failActionCreator: getCertNew.failure,
          args: [action.payload],
        })
      );

    case getType(getCertNew.success):
      if (action.payload.status === 200) {
        if (action.payload.template) {
          const selectedTemplate = action.payload.template;
          return {
            ...state,
            initLoading: false,
            selectedTemplate,
            certNew: action.payload.data,
            event: {
              selected: "",
              participants: [],
              selectedParticipants: [],
            },
            certSource: {
              ...state.certSource,
              selected: "",
            },
          };
        }
        return {
          ...state,
          initLoading: false,
          certNew: action.payload.data,
          event: {
            selected: "",
            participants: [],
            selectedParticipants: [],
          },
          certSource: {
            ...state.certSource,
            selected: "",
          },
        };
      } else {
        return { ...state, initLoading: false };
      }

    case getType(getCertNew.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(importParticipants):
      return {
        ...state,
        sendMultiple: true,
        event: {
          ...state.event,
          participants: action.payload,
          selectedParticipants: [],
        },
      };

    case getType(resendCert.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(certService.resend, {
          successActionCreator: resendCert.success,
          failActionCreator: resendCert.failure,
          args: [action.payload],
        })
      );

    case getType(resendCert.success):
      return loop(
        { ...state, initLoading: false },
        Cmd.run(historyService.goto, {
          args: [`/certsent`],
        })
      );

    case getType(resendCert.failure):
      return { ...state, initLoading: false };

    case getType(changeEvent):
      if (action.payload.eventSource.toLowerCase() === "lyyti") {
        return loop(
          state,
          Cmd.action({
            type: "START_LYYTIPARTICIPANTS_FETCH",
            payload: action.payload.eventName,
          })
        );
      }
      return loop(
        state,
        Cmd.action({
          type: "GET_EVENT_PARTICIPANTS",
          payload: {
            id: action.payload.eventId,
            name: action.payload.eventName,
          },
        })
      );

    case getType(getAllCerts.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(certService.getAllCerts, {
          successActionCreator: getAllCerts.success,
          failActionCreator: getAllCerts.failure,
        })
      );

    case getType(getAllCerts.success):
      if (action.payload.status === 200) {
        ConsoleHelper(action.payload.data);
        const allCerts = sortByKey(
          "certOperationId",
          true,
          action.payload.data
        );
        return {
          ...state,
          initLoading: false,
          allCerts,
        };
      } else {
        return { ...state, initLoading: false };
      }

    case getType(getAllCerts.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getAllLyytiEventsAsync.request):
      return {
        ...state,
        initLoading: true,
      };

    case getType(getAllLyytiEventsAsync.success):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getAllLyytiEventsAsync.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getAllEventsAsync.request):
      return {
        ...state,
        initLoading: true,
      };

    case getType(getAllEventsAsync.success):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getAllEventsAsync.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getAllLyytiParticipantsAsync.request):
      return {
        ...state,
        initLoading: action.payload.length === 0 ? false : true,
        event: {
          ...state.event,
          selected: action.payload,
        },
      };

    case getType(getAllLyytiParticipantsAsync.success):
      ConsoleHelper(action.payload.data.results);
      const participants = Object.keys(action.payload.data.results).map((p) =>
        mapToLyytiParticipant(action.payload.data.results[p])
      );
      const selectedParticipants = participants.map((p) => p.uuid);
      return {
        ...state,
        initLoading: false,
        event: {
          ...state.event,
          participants,
          selectedParticipants,
        },
      };

    case getType(getAllLyytiParticipantsAsync.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getParticipants.request):
      const name = action.payload.name ? action.payload.name : null;
      return {
        ...state,
        initLoading: name ? true : false,
        event: {
          ...state.event,
          selected: name,
        },
      };

    case getType(getParticipants.success):
      if (!state.event.selected) {
        return { ...state, initLoading: false };
      }

      //const eventName = state.event.selected.split("::")[1]
      const eventParticipants = action.payload.data.map((p) =>
        convertParticipantToListFormat(p)
      );
      const selectedEventParticipants = eventParticipants.map((p) => p.uuid);
      return {
        ...state,
        initLoading: false,
        event: {
          ...state.event,
          participants: eventParticipants,
          selectedParticipants: selectedEventParticipants,
        },
      };

    case getType(getParticipants.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(getLyytikey.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(eventService.getLyytikey, {
          successActionCreator: getLyytikey.success,
          failActionCreator: getLyytikey.failure,
        })
      );

    case getType(getLyytikey.success):
      if (action.payload.status === 200) {
        const options = state.certSource.options.includes("Lyyti")
          ? state.certSource.options
          : state.certSource.options.concat("Lyyti");
        return {
          ...state,
          initLoading: false,
          certSource: {
            ...state.certSource,
            options,
          },
        };
      } else {
        return {
          ...state,
          initLoading: false,
        };
      }

    case getType(getLyytikey.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(newCertSend.success):
      ConsoleHelper(action.payload.data);
      if (action.payload.status === 200) {
        //TODO: Virheviestikäsittely!
        if (action.payload.data.status.toLowerCase() === "preview") {
          if (!action.payload.data.url) {
            return { ...state, initLoading: false };
          }
          window.open(action.payload.data.url);
          return { ...state, initLoading: false };
        }
        const { certNew, ...rest } = state;
        return loop(
          {
            ...rest,
            selectedTemplate: "",
            certSource: {
              options: startingSourceOptions,
              selected: "",
            },
            event: {
              selected: "",
              participants: [],
              selectedParticipants: [],
            },
            sendMultiple: false,
            allCerts: [],
          },
          Cmd.run(historyService.goto, {
            args: [`/certs`],
          })
        );
      } else {
        return { ...state, initLoading: false };
      }

    case getType(newCertSend.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(startNewCertSend):
      if (state.sendMultiple && state.event.selectedParticipants.length === 0) {
        return state;
        //TODO: Tänne action jota seurataan myös error-reducerissa. Siellä laitetaan viesti "Ei valittu yhtään vastaanottajaa tms."
        //Tai sitten tämä käsitellään jo näkymän puolella, ehkä siellä parempi :p
      }
      const payload = {
        certOperationId: state.certNew.certOperationId,
        certOperationType: state.sendMultiple ? "multiSend" : "singleSend",
        template: state.certNew
          ? state.certNew.customerCertTemplates.find(
              (ct) => ct.templateId === action.payload.id
            )
          : emptyCustomerTemplate,
        selected: state.event.participants.filter((p) =>
          state.event.selectedParticipants.includes(p.uuid)
        ),
        preview: action.payload.preview,
      };

      ConsoleHelper("here starting send action with payload", payload);

      return loop(
        { ...state, initLoading: true },
        Cmd.action({ type: "START_CERT_SEND_REQUEST", payload })
      );

    case getType(changeSendType):
      return {
        ...state,
        sendMultiple: !state.sendMultiple,
      };

    case getType(toggleRecipient):
      return {
        ...state,
        event: {
          ...state.event,
          selectedParticipants: state.event.selectedParticipants.includes(
            action.payload
          )
            ? state.event.selectedParticipants.filter(
                (uuid) => uuid !== action.payload
              )
            : state.event.selectedParticipants.concat(action.payload),
        },
      };

    case getType(toggleAllRecipients):
      return {
        ...state,
        event: {
          ...state.event,
          selectedParticipants:
            state.event.selectedParticipants.length > 0
              ? []
              : state.event.participants.map((p) => p.uuid.toString()),
        },
      };

    case getType(changeEventSource):
      return {
        ...state,
        certSource: {
          ...state.certSource,
          selected: action.payload,
        },
        sendMultiple: false,
        event: {
          ...state.event,
          participants: [],
          selectedParticipants: [],
          selected: "",
        },
      };

    case getType(mapCertEditData):
      const cert = state.allCerts.find(
        (cert) => cert.certOperationId === action.payload.certOperationId
      );
      const mail = cert.mail.find(
        (mail) => mail.mailId === action.payload.mailId
      );
      const templateData = mail.rowData.templateData;
      const postmarkTemplateData = mail.rowData.postmarkTemplateData;
      const certNewEditData = { ...templateData, ...postmarkTemplateData };
      return loop(
        {
          ...state,
        },
        Cmd.list([
          Cmd.action({
            type: "START_CERT_NEW_FETCH",
            payload: cert.templateName,
          }),
          Cmd.action({
            type: "SET_FORM_VALUES",
            payload: { id: "newCert", data: certNewEditData },
          }),
          Cmd.run(historyService.goto, {
            args: [
              `/certs/${action.payload.certOperationId}/${action.payload.mailId}/edit`,
            ],
          }),
        ])
      );

    case getType(setErrorMessageMissingCertFields):
      return { ...state, initLoading: false };

    case getType(changeSelectedTemplate):
      const template = state.certNew.customerCertTemplates.find(
        (t) => t.templateName === action.payload
      );
      if (!template) {
        return loop(
          {
            ...state,
            selectedTemplate: action.payload,
          },
          Cmd.action({ type: "SET_CERT_TEMPLATE_DEFAULT_VALUES", payload: [] })
        );
      }

      const defaultValues = template.postmarkTemplateDataModel.postmarkTemplateQuestions
        .filter((q) => q.defaultValue)
        .map((q) => {
          return { id: q.id, defaultValue: q.defaultValue };
        })
        .concat(
          template.templateDataModel.templateQuestions
            .filter((q) => q.defaultValue)
            .map((q) => {
              return { id: q.id, defaultValue: q.defaultValue };
            })
        );

      return loop(
        {
          ...state,
          selectedTemplate: action.payload,
        },
        Cmd.action({
          type: "SET_CERT_TEMPLATE_DEFAULT_VALUES",
          payload: defaultValues,
        })
      );

    default:
      return state;
  }
};

export function mapToLyytiParticipant(participant: any): lyytiParticipant {
  const participantObject = Object.keys(participant.answers).reduce(
    (p, key) => ({
      ...p,
      [key]: participant.answers[key].answer
        ? participant.answers[key].answer
        : "",
    }),
    {}
  );
  return {
    ...participantObject,
    status: participant.status,
    uuid: participant.uid.toString(),
  };
}

export type SelectedCustomerTemplate = {
  template: CustomerCertTemplate;
  found: boolean;
};
export function getCustomerCertTemplate(
  state,
  id: string
): SelectedCustomerTemplate {
  const result =
    state.certState.certNew &&
    state.certState.certNew.customerCertTemplates.find(
      (ct) => ct.templateName === id
    )
      ? {
          template: state.certState.certNew.customerCertTemplates.find(
            (ct) => ct.templateName === id
          ),
          found: true,
        }
      : { template: emptyCustomerTemplate, found: false };
  return result;
}

export function getCertDetails(state, id: string): CertMailTable[] {
  const foundCert: Cert = state.certState.allCerts.find(
    (cert) => cert.certOperationId === id
  );
  const result =
    state.certState.allCerts && foundCert
      ? foundCert.mail.map((mailDetails) => {
          const { mailTemplateModel, rowData, ...rest } = mailDetails;
          const templateData: {
            [key: string]: string | string[];
          } = Object.assign(
            {},
            ...Object.keys(mailTemplateModel).map((key) => {
              return { [`DATA__${key}`]: mailTemplateModel[key] };
            })
          );
          return { ...rest, ...templateData };
        })
      : [];
  return result;
}

export function getCertTemplateSelectOptions(state): string[] {
  const options = state.certState.certNew
    ? state.certState.certNew.customerCertTemplates.map((c) => c.templateName)
    : [];
  return options;
}

export function getCertOperationId(state): string {
  return state.certState.certNew ? state.certState.certNew.certOperationId : "";
}

export function loadingStatus(state): boolean {
  return state.certState.initLoading;
}

export function getEventSourceOptions(state): string[] {
  return state.certState.certSource.options;
}

export type CertPageData = {
  eventSourceOptions: string[];
  selectedTemplate: string;
  isLoading: boolean;
  certTemplateOptions: string[];
  selectedSource: CertSource;
  lyytiEvents: { name: string; id: string }[];
  events: { name: string; id: string }[];
  selectedEvent: string;
  participants: any[];
  sendMultiple: boolean;
};
export function getCertPageData(state): CertPageData {
  const certTemplateOptions = state.certState.certNew
    ? state.certState.certNew.customerCertTemplates.map((c) => c.templateName)
    : [];
  const isLoading = state.certState.initLoading;
  const eventSourceOptions = state.certState.certSource.options;
  const selectedSource = state.certState.certSource.selected;
  const lyytiEvents =
    state.listEventsState.lyytiEvents.length > 0
      ? state.listEventsState.lyytiEvents.map((l) => {
          return { name: l.name, id: l.eid };
        })
      : [];
  const events =
    state.listEventsState.events.length > 0
      ? state.listEventsState.events.map((e) => {
          return { name: `${e.eventId}::${e.eventName}`, id: e.eventId };
        })
      : [];
  const selectedEvent = state.certState.event.selected;
  const participants = state.certState.event.participants;
  const sendMultiple = state.certState.sendMultiple;
  const selectedTemplate = state.certState.selectedTemplate;
  return {
    certTemplateOptions,
    isLoading,
    eventSourceOptions,
    selectedSource,
    events,
    lyytiEvents,
    selectedEvent,
    participants,
    sendMultiple,
    selectedTemplate,
  };
}

export function getSelectedParticipants(state): string[] {
  return state.certState.event.selectedParticipants;
}

export type SendMultipleCerts = {
  sendMultiple: boolean;
  personQuestionIds: string[];
};
export function sendMultiple(state): SendMultipleCerts {
  const selected =
    state.certState.event.selectedParticipants.length > 0
      ? state.certState.event.participants.filter((p) =>
          state.certState.event.selectedParticipants.includes(p.uuid)
        )
      : state.certState.event.participants;
  const personQuestionIds = [""].concat(
    Object.keys(
      selected.reduce((result, obj) => {
        return Object.assign(result, obj);
      }, {})
    )
  );
  return { sendMultiple: state.certState.sendMultiple, personQuestionIds };
}

export function getAllCustomerCerts(
  state
): { data: any[]; initLoading: boolean } {
  return {
    data: state.certState.allCerts,
    initLoading: state.certState.initLoading,
  };
}
